/**
 * HoldingsTable — holdings table grouped/sorted by market value.
 *
 * Phase 3 Task 3.3 (REQ-DASH-001, 2026-05-17): rewired off the Anna
 * `HoldingItem[]` fixture onto the backend `AssetClassBreakdown[]` payload
 * returned by `useDashboardHoldings`. Renders one row per holding (flattened
 * across asset classes, sorted by class total then by market value).
 *
 * Valuations Wave 1 (F2, 2026-07-04): the hardcoded flat `[50×12]` sparkline +
 * synthetic "0.0%" return pill are GONE. Each row lazily fetches its REAL
 * valuation series (`useHoldingValuations`) — at most `CONCURRENT_FETCHES`
 * in flight at a time (rows unlock as earlier ones settle) so a 40-holding
 * trust doesn't fire 40 simultaneous requests. Honesty rules:
 *   - history < 2 points  → "Not valued" in the trend cell (no fake flat line);
 *   - no data point ≥ ~12 months old → "None" in the 1-yr return cell
 *     (never a synthetic 0.0%).
 * A per-row `<ValueProvenance>` chip (F1) carries basis + as-at + staleness.
 * (Follow-up noted in the Wave-1 spec: if per-row fetches measurably hurt on
 * large books, add a batch valuations endpoint — do not fan out further.)
 *
 * The wrapper (`dashboard.tsx`) owns loading + error states so this component
 * remains a presentational view over the flattened rows; the empty state is
 * this component's own, because only it knows the rows flattened to nothing.
 *
 * Discoverability fix (2026-08-01): this is the FIRST place a trustee sees
 * their holdings, yet it had no way to reach the dated valuation history
 * behind the sparkline/provenance chip — only /properties, /companies and
 * /portfolios (via `HoldingRowActions`) could open it. Each row now offers a
 * "Valuation history" link that reuses `ValuationHistoryModal` from
 * holding-row-actions.tsx (no rebuild) via the series this row already
 * fetches for its own sparkline — so opening it is a cache hit, not a new
 * request. Read-only (no Revalue/Edit/Delete): this table stays presentational.
 *
 * ── COHESION PASS, SCREEN 1a (2026-09-15) ───────────────────────────────────
 *
 * It now draws through `BleedTable`, the one table in the product, so the
 * dashboard's book and the firm console's book are the same object rather than
 * two hand-rolled head rows that drifted. That change carries two consequences
 * worth naming, both of them the point:
 *
 *   · HEADERS ARE LEFT-ALIGNED over left-aligned cells, numeric columns
 *     included. This table right-aligned four of its five headers. Column
 *     alignment was raised repeatedly in the 15 September review, and
 *     `BleedTable` decides it from the column's KIND so a header cannot
 *     disagree with its cells.
 *   · THE ROWS ARE DATA, NOT COMPONENTS. `BleedTable` takes cells, so the
 *     per-row valuation fetch could no longer live in a `<tr>` component.
 *     It lives in `useHoldingSeries` below, called by each of the three cells
 *     that need it. That is ONE request per holding, not three: all three
 *     calls share the `useHoldingValuations` query key, so react-query serves
 *     the second and third from the same cache entry. The concurrency window
 *     is unchanged, and `onSettled` de-duplicates on a Set, so three
 *     consumers reporting the same row settled still counts once.
 *
 * Contract: docs/redesign-20260915/design_handoff_app_cohesion/README.md,
 * "Tables" and screen 1a.
 */

import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { Sparkline } from '../charts/sparkline';
import { Icon } from '../icon';
import type { IconName } from '../icon';
import { ValueProvenance } from '../value-provenance';
import { ValuationHistoryModal, linkBtn } from '../holding-row-actions';
import { BleedTable, type BleedColumn, type BleedRow } from '../bleed-table';
import { StateCard } from '../state-card';
import {
  assetClassColorVar,
  assetClassLabel,
  formatBps,
  formatZarCents,
  useHoldingValuations,
  type AssetClassBreakdown,
  type HoldingAssetClass,
  type ValuationSeriesPoint,
} from '../../lib/api';

/** Max in-flight per-row valuation fetches. Rows beyond the window enable as
 *  earlier rows settle, so a long book loads its sparks in calm batches. */
const CONCURRENT_FETCHES = 4;

type PerfState = 'pos' | 'neg' | 'flat';

interface PerfPillProps {
  value: number;
  state: PerfState;
}

function PerfPill({ value, state }: PerfPillProps) {
  const sign = value > 0 ? '+' : '';
  const ic: IconName =
    state === 'pos' ? 'arrow-up' : state === 'neg' ? 'arrow-down' : 'flat';
  return (
    <span className={'perf-pill ' + state}>
      <Icon name={ic} size={11} stroke={2} />
      {sign}
      {value.toFixed(1)}%
    </span>
  );
}

export interface HoldingsTableProps {
  byAssetClass: AssetClassBreakdown[];
  totalMarketValueZar: number;
  /** The owning trust — enables the per-row REAL valuation-series fetch (F2).
   *  Absent (legacy call sites / no trust yet): rows render honest "Not valued" / "None" text. */
  trustId?: string;
  /**
   * The door to the full register, rendered on the right of the table's footer
   * line. Passed IN rather than built here so this component needs no router
   * context: it is rendered directly by three test files and by the design
   * harness, and a `<Link>` inside it made all of them require a router just to
   * draw a table.
   */
  footerLink?: ReactNode;
}

interface FlatRow {
  id: string;
  assetName: string;
  assetClass: HoldingAssetClass;
  tag: string;
  marketValueZar: number;
  weightBps: number;
  asAt: string;
}

function flatten(byAssetClass: AssetClassBreakdown[]): FlatRow[] {
  const out: FlatRow[] = [];
  for (const cls of byAssetClass) {
    for (const h of cls.holdings) {
      out.push({
        id: h.id,
        assetName: h.assetName,
        assetClass: cls.assetClass,
        tag: h.isin ?? h.custodian ?? assetClassLabel(cls.assetClass),
        marketValueZar: h.marketValueZar,
        weightBps: h.weightBps,
        asAt: h.asAt,
      });
    }
  }
  return out;
}

/** 1-yr return from the REAL series: latest value vs the newest point at least
 *  ~12 months old. Null (→ "None") when the series has no 12-month-old data —
 *  never a synthetic 0.0%. Series arrives newest-first; values are whole Rand
 *  (relative maths only, so the unit cancels). PURE. */
export function oneYearReturnPct(
  series: ValuationSeriesPoint[],
  now: Date = new Date(),
): number | null {
  if (series.length < 2) return null;
  const latest = series[0]!;
  const cutoff = new Date(now);
  cutoff.setMonth(cutoff.getMonth() - 12);
  // Newest-first: the first point at/before the cutoff is the closest baseline.
  const baseline = series.find((p) => new Date(p.asAt).getTime() <= cutoff.getTime());
  if (!baseline || baseline.marketValueRand <= 0) return null;
  return ((latest.marketValueRand - baseline.marketValueRand) / baseline.marketValueRand) * 100;
}

interface CellProps {
  row: FlatRow;
  trustId: string | undefined;
  enabled: boolean;
  onSettled: (id: string) => void;
}

/**
 * The one read of a holding's valuation series, shared by the three cells that
 * need it. Three callers, ONE request: the query key is the same for all of
 * them, so react-query serves the rest from its cache.
 */
function useHoldingSeries({ row, trustId, enabled, onSettled }: CellProps) {
  const valsQ = useHoldingValuations({
    trustId: trustId ?? '',
    holdingId: row.id,
    enabled: enabled && !!trustId,
  });
  const fetchSettled = valsQ.isSuccess || valsQ.isError;
  useEffect(() => {
    if (fetchSettled) onSettled(row.id);
  }, [fetchSettled, onSettled, row.id]);
  return valsQ;
}

/** The holding's name, class and tag, its provenance chip, and the door to its
 *  dated valuation history. */
function HoldingCell(props: CellProps) {
  const { row, trustId } = props;
  const valsQ = useHoldingSeries(props);
  // Discoverability fix (2026-08-01): see the file header.
  const [showHistory, setShowHistory] = useState(false);
  const latest = valsQ.data?.series?.[0];

  return (
    <>
      <div className="bleed-table-entity">{row.assetName}</div>
      <div className="bleed-table-sub">
        {assetClassLabel(row.assetClass)}{' '}
        <span className="bleed-table-mono" style={{ display: 'inline' }}>
          {row.tag}
        </span>
      </div>
      {/* F1: provenance chip — basis + as-at + staleness, from the real series
          when loaded; the row's own asAt (basis inferred, §2.1) when the series
          isn't available. Hidden while in flight so a feed-valued row never
          flashes an inferred "Owner estimate". */}
      <div style={{ marginTop: 4 }}>
        {valsQ.data ? (
          <ValueProvenance
            basis={valsQ.data.latestBasis}
            source={valsQ.data.latestSource}
            asAt={valsQ.data.lastValuedAt}
            stale={valsQ.data.stale}
            viaFirmActAs={latest?.viaFirmActAs}
            compact
          />
        ) : valsQ.isError || !trustId ? (
          <ValueProvenance asAt={row.asAt} compact />
        ) : null}
      </div>
      {/* Discoverability fix (2026-08-01): the dated history behind the
          sparkline was previously unreachable from this table. */}
      {trustId && (
        <div style={{ marginTop: 4 }}>
          {/* testid is surface-specific. HoldingRowActions, this table and
              values-attention-card can all show the SAME holding, and two of
              them render on the dashboard together — a shared testid makes a
              getByTestId query match more than one node. One prefix per
              surface keeps every query unambiguous. */}
          <button
            type="button"
            data-testid={`history-holding-table-${row.id}`}
            onClick={() => setShowHistory(true)}
            style={{ ...linkBtn, color: 'var(--ink)', fontSize: 11.5 }}
          >
            Valuation history
          </button>
        </div>
      )}
      {showHistory && trustId && (
        <ValuationHistoryModal
          trustId={trustId}
          holdingId={row.id}
          name={row.assetName}
          onClose={() => setShowHistory(false)}
        />
      )}
    </>
  );
}

/** The 12-month spark, or the honest "Not valued" when there is no series to
 *  draw. Never a flat line standing in for missing history. */
function TrendCell(props: CellProps) {
  const valsQ = useHoldingSeries(props);
  const series = valsQ.data?.series ?? [];
  // Newest-first on the wire; the spark wants chronological.
  const sparkValues = [...series].reverse().map((p) => p.marketValueRand);
  if (sparkValues.length < 2) {
    return (
      <span style={{ color: 'var(--ink-4)', fontSize: 13 }} aria-label="No valuation history yet">
        Not valued
      </span>
    );
  }
  return (
    <Sparkline
      data={sparkValues}
      color={assetClassColorVar(props.row.assetClass)}
      width={100}
      height={28}
    />
  );
}

/** The 1-year return, or "None" when the series has no 12-month-old point.
 *  Never a synthetic 0.0%. */
function ReturnCell(props: CellProps) {
  const valsQ = useHoldingSeries(props);
  const perfPct = oneYearReturnPct(valsQ.data?.series ?? []);
  if (perfPct == null) {
    return (
      <span style={{ color: 'var(--ink-4)', fontSize: 13 }} aria-label="No 12-month comparison yet">
        None
      </span>
    );
  }
  const perfState: PerfState = perfPct > 0 ? 'pos' : perfPct < 0 ? 'neg' : 'flat';
  return <PerfPill value={perfPct} state={perfState} />;
}

const COLUMNS: BleedColumn[] = [
  { key: 'holding', header: 'Holding' },
  { key: 'trend', header: '12-month trend' },
  { key: 'value', header: 'Value', kind: 'num' },
  { key: 'weight', header: 'Share of trust', kind: 'num' },
  { key: 'return', header: '1-year return', kind: 'num' },
];

export function HoldingsTable({
  byAssetClass,
  totalMarketValueZar,
  trustId,
  footerLink,
}: HoldingsTableProps) {
  const rows = flatten(byAssetClass);

  // Concurrency window: row i may fetch once i < CONCURRENT_FETCHES + settled
  // count. Settled ids are tracked in a Set so a re-render / refetch never
  // double-counts a row — which is also what makes three cells per row sharing
  // one query safe to report independently.
  const [settledIds, setSettledIds] = useState<ReadonlySet<string>>(new Set());
  const onSettled = useCallback((id: string) => {
    setSettledIds((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  if (rows.length === 0) {
    return (
      <StateCard
        variant="empty"
        testId="holdings-empty"
        label="The book"
        headline="No holdings on file for this trust yet."
        detail="Once the opening valuations are recorded, or a custodian feed lands, rows appear here grouped by asset class."
      />
    );
  }

  const tableRows: BleedRow[] = rows.map((row, i) => {
    const cellProps: CellProps = {
      row,
      trustId,
      enabled: i < CONCURRENT_FETCHES + settledIds.size,
      onSettled,
    };
    return {
      key: row.id,
      cells: {
        holding: <HoldingCell {...cellProps} />,
        trend: <TrendCell {...cellProps} />,
        value: formatZarCents(row.marketValueZar),
        weight: totalMarketValueZar === 0 ? '0.00%' : formatBps(row.weightBps),
        return: <ReturnCell {...cellProps} />,
      },
    };
  });

  return (
    <BleedTable
      testId="holdings"
      label="Holdings and performance"
      columns={COLUMNS}
      rows={tableRows}
      footer={
        <>
          <span>
            {rows.length} holding{rows.length === 1 ? '' : 's'}
          </span>
          {/* The contract's footer line. It is also the only door from this
              card to the full register, which design gate B1 asks for: a
              feature that exists only at a URL does not exist. */}
          {footerLink ?? null}
        </>
      }
    />
  );
}
